/**
 * Las reglas de edad para participar.
 *
 * Estaban escritas dos veces dentro del formulario de persona natural —una al
 * escoger la fecha de nacimiento y otra al enviar— y habian empezado a
 * separarse: la misma regla decia "no acepta participantes menores de edad"
 * en un lado y "no se aceptan" en el otro.
 *
 * Quien decide si se admiten menores es la convocatoria, con su casilla
 * menor_edad. Las demas son rangos propios de unas lineas de la Secretaria de
 * las Mujeres, que se reconocen por el nombre de la linea.
 */

import moment from "moment";

export type ContextoEdad = {
  /** La casilla menor_edad de la convocatoria. */
  aceptaMenores?: boolean;
  /** El nombre de la linea, de donde salen los rangos de las lineas SM. */
  nombreLinea?: string;
};

export const EDAD_MAXIMA = 100;

/** Rangos propios de algunas lineas, por el nombre con el que se reconocen. */
const RANGOS_POR_LINEA: {
  patron: string;
  minima?: number;
  maxima?: number;
  mensaje: string;
}[] = [
  {
    patron: "Categoría Junior – Concurso Mujeres Jóvenes Talento - SM",
    minima: 14,
    maxima: 17,
    mensaje: "Para la categoría Junior la edad debe estar entre 14 y 17 años",
  },
  {
    patron: "Concurso Mujeres Jóvenes Talento - SM",
    minima: 18,
    maxima: 28,
    mensaje: "Para el Concurso Mujeres Jóvenes Talento la edad debe estar entre 18 y 28 años",
  },
  {
    patron: "Reconocimiento al Liderazgo de las Mujeres de Medellín - SM",
    minima: 29,
    mensaje: "Para este reconocimiento la edad debe ser de 29 años en adelante",
  },
  {
    patron: "Mención especial On.Going: mujeres que mueven Medellín - SM",
    minima: 29,
    mensaje: "Para este reconocimiento la edad debe ser de 29 años en adelante",
  },
  {
    patron:
      "Mención especial Universidad EIA: Mujeres líderes en ciencia, tecnología e innovación - SM",
    minima: 29,
    mensaje: "Para este reconocimiento la edad debe ser de 29 años en adelante",
  },
];

/** Los años cumplidos a esa fecha de nacimiento. */
export const edadCumplida = (fechaNacimiento: any, ahora: any = moment()): number | null => {
  if (!fechaNacimiento) return null;

  const nacimiento = moment(fechaNacimiento);
  if (!nacimiento.isValid()) return null;

  return moment(ahora).diff(nacimiento, "years");
};

/**
 * Que impide participar con esa fecha de nacimiento, o null si nada.
 *
 * El orden es el que tenia el formulario: primero lo que no depende de la
 * linea. La linea Junior admite menores, asi que su convocatoria tiene que
 * traer menor_edad marcada; si no, esa regla general corta antes.
 */
export const problemaDeEdad = (
  fechaNacimiento: any,
  contexto: ContextoEdad = {},
  ahora: any = moment()
): string | null => {
  if (!fechaNacimiento) return null;

  const nacimiento = moment(fechaNacimiento);
  if (!nacimiento.isValid()) return null;

  if (nacimiento.isAfter(moment(ahora))) {
    return "La fecha seleccionada es mayor a la actual";
  }

  const edad = edadCumplida(fechaNacimiento, ahora) as number;

  if (!contexto.aceptaMenores && edad < 18) {
    return "En esta convocatoria no se aceptan participantes menores de edad";
  }

  if (edad >= EDAD_MAXIMA) {
    return `No puedes escoger fechas mayores a ${EDAD_MAXIMA} años`;
  }

  const nombreLinea = String(contexto.nombreLinea ?? "");
  const rango = RANGOS_POR_LINEA.find((r) => nombreLinea.includes(r.patron));
  if (!rango) return null;

  const porDebajo = rango.minima !== undefined && edad < rango.minima;
  const porEncima = rango.maxima !== undefined && edad > rango.maxima;

  return porDebajo || porEncima ? rango.mensaje : null;
};
